<template>
    <div>
        <!-- hover-sensitive area -->
        <div
            v-if="isSideMenuHidden"
            class="sidebar-hover-area"
            @mouseenter="handleHoverShowSidebar"
            @mouseleave="clearHoverTimer"
        ></div>

        <!-- fixed side menu -->
        <transition name="slide">
            <div
                id="dsv-app-sidemenu"
                class="dsv-sidemenu"
                :class="{ hidden: isSideMenuHidden, 'hover-mode': isHoverMode }"
                @mouseleave="clearHoverTimer"
            >
                <div class="menu-header">
                    <img src="assets/daysmartvet_logo_dark.svg" />
                    <span>DaySmart Vet</span>

                    <button
                        @click="toggleSideMenu"
                        title="Toggle Side Menu"
                        class="toggleSide"
                    >
                        <i class="far fa-caret-square-left"></i>
                    </button>
                </div>

                <div class="menu-body d-flex flex-column hide-scrollbar">
                    <div class="nav-utility-container">
                        <nav-utility :is-nav-horizontal="false" />
                    </div>
                    <div class="navigations">
                        <div class="navigations-label">
                            Navigations
                        </div>
                        <ul>
                            <side-menu-item
                                v-for="(item, index) in accessibleMenuItems"
                                :key="index"
                                :item="item"
                                :level="0"
                            ></side-menu-item>
                        </ul>
                        <!-- <button class="root" v-if="_hasAccess('setting.access')">
                                <module-icon class="icon" name="settings" />
                                <a :href="env.APPS + 'business/business'">
                                    Settings
                                </a>
                                <i class="fa fa-chevron-right chevron"></i>
                            </button> -->
                    </div>
                </div>
                <div class="menu-footer">
                    <nav-utility-profile
                        v-if="!_isDesktopView"
                        class="w-100"
                        :current-actor="$root.currentActor"
                        :is-nav-horizontal="false"
                    />
                </div>
            </div>
        </transition>

        <!-- content -->
        <div
            class="dsv-content"
            :class="[
                { full: isSideMenuHidden },
                { 'hover-mode': isHoverMode },
                { 'ci-render-left': isCiRoute }
            ]"
        >
            <slot></slot>
        </div>
    </div>
</template>
<script>
import NavUtility from "@/module/layout/nav-utility.vue";
import NavUtilityProfile from "@/module/layout/nav-utility-profile.vue";
import SideMenuItem from "@/module/layout/side-menu-item.vue";
import Helper from "@/module/layout/helper";

export default {
    name: "AppLayoutEnhanced",
    mixins: [Helper],
    components: {
        SideMenuItem,
        NavUtilityProfile,
        NavUtility
    },
    data() {
        return {
            hoverTimer: null,
            expandedIndex: null,
            currentShortcut: null
        };
    },
    computed: {
        isEnhancedLayout() {
            return this.$store.state.isEnhancedLayout;
        },
        isSideMenuHidden() {
            return this.$store.state.isSideMenuHidden;
        },
        isHoverMode() {
            return this.$store.state.isHoverMode || false;
        },
        isCiRoute() {
            return (
                this.$route.path.includes("settings-ci") ||
                this.$route.path.includes("report-ci")
            );
        },
        accessibleMenuItems() {
            return this.filteredMenuItems.filter(item => {
                const hasAccess = item.access
                    ? this._hasAccess(item.access)
                    : true;
                const isEnabled = item.isExtensionEnabled
                    ? this._isExtensionEnabled(item.isExtensionEnabled)
                    : true;

                return hasAccess && isEnabled;
            });
        }
    },
    watch: {
        isSideMenuHidden() {
            this.setContentScrollBehavior();
        }
    },
    mounted() {
        this.setContentScrollBehavior();
    },
    methods: {
        toggleSideMenu() {
            this.$store.commit("setHoverMode", false); // reset hover mode when manually toggled
            this.$store.commit("toggleSideMenu");
        },
        handleHoverShowSidebar() {
            this.hoverTimer = setTimeout(() => {
                this.$store.commit("setHoverMode", true);
                this.$store.commit("toggleSideMenu");
            }, 300);
        },
        clearHoverTimer() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);

                if (this.isHoverMode && !this.isSideMenuHidden) {
                    setTimeout(() => {
                        this.$store.commit("setHoverMode", false);
                        this.$store.commit("toggleSideMenu");
                    }, 300);
                }
            }
        },
        setContentScrollBehavior() {
            const mq = matchMedia("(max-width: 767px)");
            if (mq.matches) {
                document.body.style.overflow =
                    this.isSideMenuHidden === true ? "scroll" : "hidden";
            }
        }
    }
};
</script>
<style lang="scss" scoped>
$top-bar-secondary-height: 52px;
$footer-height: 72px;

.dsv-sidemenu {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    bottom: 0;
    background: #190d30;
    overflow-y: hidden;
    overscroll-behavior-y: contain;
    transition: transform 0.3s ease;
    z-index: 1002;

    &.hidden {
        transform: translateX(-100%);
    }

    &.hover-mode {
        box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
    }

    .menu-header {
        position: relative;
        display: flex;
        align-items: center;
        height: 56px;
        gap: 10px;
        padding: 12px 20px 8px;
        background: #190d30;

        img {
            width: 28px;
        }

        span {
            flex-grow: 1;
            font-size: 1.025rem;
            font-weight: 500;
            color: #fff;
        }

        button.toggleSide {
            background: none;
            border: none;
            outline: none;
            padding: 10px;
            font-size: 1.4em;
            color: #fff;
        }

        .menu-header-item-mobile {
            display: flex;
            gap: 8px;
            align-items: center;
        }
    }

    .menu-body {
        overflow-y: scroll;
        overscroll-behavior: contain;
        height: calc(100vh - #{$top-bar-secondary-height} - #{$footer-height});

        .navigations {
            margin-top: 8px;
            padding: 8px 14px;

            ul {
                display: flex;
                flex-direction: column;
                margin: 0;
                padding: 0;
                list-style: none;
            }
        }

        .navigations-label {
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 600;
            height: 32px;
            align-content: center;
            color: #fff;
        }

        .nav-utility-container {
            padding: 8px 14px;
            width: 100%;

            ::v-deep #nav-item-business > .nav-link {
                color: #fff;
            }
        }
    }

    .menu-header:has(.search-dialog-container.visible) + .menu-body {
        overflow-y: hidden;
    }

    .menu-footer {
        position: fixed;
        bottom: 0;
        display: flex;
        background: #190d30;
        width: 100%;
        height: $footer-height;
        padding: 8px 16px;

        ::v-deep ul {
            list-style: none;
        }

        ::v-deep li {
            list-style: none;
        }

        ::v-deep #nav-item-profile > .nav-link {
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.1);

            .actor {
                padding: 0;
            }
        }
    }
}

.sidebar-hover-area {
    position: fixed;
    top: 0;
    left: 0;
    width: 5px;
    height: 100vh;
    z-index: 998;
    background: transparent;

    &:hover {
        background: rgba(0, 0, 0, 0.2);
        transition: width 0.2s ease;
        width: 10px;
        cursor: pointer;
    }
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
}

.dsv-content {
    padding-top: $top-bar-secondary-height;
    margin-left: 0;
    min-height: calc(100vh - #{$top-bar-secondary-height});
    background: #f0f0f0;
    transition: margin-left 0.3s ease;

    &.full {
        margin-left: 0;
    }

    &.hover-mode {
        margin-left: 0 !important;
    }

    &.ci-render-left {
        margin-top: 0;
        padding-top: 0;
        min-height: unset;
    }
}

@media (min-width: 501px) {
    .dsv-sidemenu {
        width: 280px;

        .toggleSide {
            display: none;
        }

        .menu-footer {
            width: 280px;
        }
    }

    .dsv-content {
        margin-left: 280px;
    }
}

@media (min-width: 1400px) {
    .dsv-sidemenu {
        .menu-header-item-mobile {
            display: none;
        }

        .nav-utility-container {
            display: none;
        }

        .menu-body {
            height: calc(100vh - #{$top-bar-secondary-height});
        }

        .menu-footer {
            display: none;
        }
    }
}
/* Apply to the element you want to hide the scrollbar on, e.g., body */
.hide-scrollbar {
    overflow: auto; /* or scroll, depending on your layout */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
}

/* Chrome, Safari, Opera */
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}
</style>
